import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, CalendarX2, CheckCircle2, CreditCard, Loader2, PartyPopper, Send } from "lucide-react";
import { api, formatApiError } from "@/auth";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-accent";
const labelCls = "mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400";

const EMPTY = {
  first_name: "",
  last_name: "",
  birth_date: "",
  email: "",
  phone: "",
  modality: "",
  session: "",
  guardian_name: "",
  guardian_email: "",
  guardian_phone: "",
  guardian_accepted: false,
  accept_terms: false,
  accept_privacy: false,
  consent_images: false,
};

const isMinor = (birthDate) => {
  if (!birthDate) return false;
  const bd = new Date(`${birthDate}T12:00:00`);
  const today = new Date();
  if (Number.isNaN(bd.getTime())) return false;
  const age = today.getFullYear() - bd.getFullYear();
  const beforeBirthday = today.getMonth() < bd.getMonth() || (today.getMonth() === bd.getMonth() && today.getDate() < bd.getDate());
  return age - (beforeBirthday ? 1 : 0) < 18;
};

const SESSION_LABEL = { first: "1st Dance Workshop (15:30 – 17:00)", second: "2nd Dance Workshop (18:00 – 19:30)" };

export function ConfirmedScreen({ status }) {
  return (
    <div className="mx-auto max-w-2xl rounded-2xl border-2 border-emerald-500/60 bg-card p-8 text-center shadow-[0_0_50px_rgba(16,185,129,0.25)] sm:p-12" data-testid="workshop-confirmed">
      <PartyPopper size={44} className="mx-auto text-emerald-400" />
      <h2 className="mt-5 font-display text-3xl font-black uppercase tracking-tight text-emerald-400 sm:text-4xl">
        Your place is confirmed!
      </h2>
      <p className="mt-4 text-sm leading-relaxed text-zinc-300 sm:text-base">
        ¡Tu plaza para el SPECIAL K-POP DANCE WORKSHOP con JAY KIM está confirmada!
        <br />
        Nos vemos el 21 de noviembre en K-HOUSE Dance Academy.
      </p>
      <div className="mt-6 inline-flex flex-col gap-1 rounded-lg border border-white/10 bg-panel px-6 py-4 font-mono text-xs text-zinc-300">
        <span>
          Referencia: <strong className="text-accent">{status?.reference}</strong>
        </span>
        <span>
          {status?.modality === "full" ? "Full Experience · 35€" : `Single Workshop · ${status?.amount_eur}€`}
        </span>
      </div>
    </div>
  );
}

export default function WorkshopForm({ config = null, onRegistered }) {
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [created, setCreated] = useState(null);
  const [statusRef, setStatusRef] = useState(params.get("ref"));
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!statusRef) return;
    let timer;
    const check = () =>
      api
        .get(`/workshop/status/${statusRef}`)
        .then((r) => setStatus(r.data))
        .catch(() => {});
    check();
    timer = setInterval(() => {
      if (status?.payment_status === "pagada") {
        clearInterval(timer);
        return;
      }
      check();
    }, 8000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusRef]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const minor = isMinor(form.birth_date);
  const total = form.modality === "full" ? 35 : form.modality === "single" ? 20 : 0;

  const chooseModality = (m) => {
    setForm((f) => ({ ...f, modality: m, session: m === "full" ? "" : f.session }));
    const next = new URLSearchParams(params);
    next.set("modalidad", m);
    setParams(next, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.modality === "single" && !form.session) {
      toast.error("Selecciona qué workshop quieres (1ª o 2ª sesión)");
      return;
    }
    setSending(true);
    try {
      // Strip empty-string optional fields that backend validates with EmailStr/Optional
      const payload = { ...form };
      if (!payload.guardian_email) delete payload.guardian_email;
      if (!payload.guardian_name) delete payload.guardian_name;
      if (!payload.guardian_phone) delete payload.guardian_phone;
      const { data } = await api.post("/workshop/register", payload);
      setCreated(data);
      onRegistered?.();
      toast.success(`Inscripción registrada · Ref ${data.reference}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (status?.payment_status === "pagada") return <ConfirmedScreen status={status} />;

  if (statusRef && status) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-amber-400/50 bg-card p-8 text-center sm:p-10" data-testid="workshop-pending">
        <CalendarX2 size={40} className="mx-auto text-amber-300" />
        <h2 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-tight text-amber-300 sm:text-3xl">
          Pago pendiente
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-zinc-300 sm:text-base">
          Tu inscripción <strong className="text-accent">{status.reference}</strong> sigue pendiente de pago. No está confirmada hasta que se reciba el pago.
          <br />
          Comprobamos el estado automáticamente…
        </p>
      </div>
    );
  }

  if (created) {
    const payLink =
      created.amount_eur === 20 ? config?.payment_url_single : created.amount_eur === 35 ? config?.payment_url_full : null;
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-accent/50 bg-card p-8 text-center sm:p-10" data-testid="workshop-created">
        <BadgeCheck size={40} className="mx-auto text-accent" />
        <h2 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">
          Inscripción registrada
        </h2>
        <p className="mt-4 text-sm text-zinc-300 sm:text-base">
          Referencia: <strong className="text-accent">{created.reference}</strong> · Importe: <strong>{created.amount_eur}€</strong>
        </p>
        {payLink ? (
          <a
            data-testid="workshop-pay-button"
            href={payLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_28px_rgba(255,0,122,0.45)] transition-all duration-300 hover:bg-accent-hover"
          >
            <CreditCard size={18} /> Ir al pago seguro · {created.amount_eur}€
          </a>
        ) : (
          <p className="mt-6 rounded-lg border border-amber-400/40 bg-amber-400/10 px-5 py-4 text-sm text-amber-200">
            La pasarela de pago del workshop aún no está activa. Tu plaza queda registrada como{" "}
            <strong>pendiente de pago</strong> — la academia te indicará cómo completar el pago.
          </p>
        )}
        <p className="mt-5 text-xs text-zinc-500">
          Te enviamos un email con los detalles. La plaza se confirma solo cuando se recibe el pago.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="workshop-form"
      onSubmit={submit}
      className="mx-auto max-w-3xl space-y-6 rounded-2xl border border-white/10 bg-panel p-6 shadow-[0_0_50px_rgba(255,0,122,0.12)] sm:p-9"
    >
      <div>
        <p className={labelCls}>Datos del participante *</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input required data-testid="wk-first-name" value={form.first_name} onChange={set("first_name")} placeholder="Nombre" className={inputCls} />
          <input required data-testid="wk-last-name" value={form.last_name} onChange={set("last_name")} placeholder="Apellidos" className={inputCls} />
          <input required data-testid="wk-birth-date" type="date" value={form.birth_date} onChange={set("birth_date")} className={inputCls} />
          <input required data-testid="wk-email" type="email" value={form.email} onChange={set("email")} placeholder="Correo electrónico" className={inputCls} />
          <input required data-testid="wk-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="Teléfono de contacto" className={`${inputCls} sm:col-span-2`} />
        </div>
        <p className="mt-2 text-[11px] text-zinc-500">No solicitamos DNI/NIE. Solo los datos necesarios para gestionar tu inscripción.</p>
      </div>

      <div>
        <p className={labelCls}>Selecciona tu modalidad *</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {[
            { id: "single", title: "Single Workshop", price: "20 €" },
            { id: "full", title: "Full Experience", price: "35 €", badge: "Experiencia completa" },
          ].map((m) => (
            <button
              type="button"
              key={m.id}
              data-testid={`wk-modality-${m.id}`}
              onClick={() => chooseModality(m.id)}
              className={`relative rounded-xl border-2 p-4 text-left transition-all duration-300 ${
                form.modality === m.id ? "border-accent bg-accent/10" : "border-white/10 bg-card hover:border-white/30"
              }`}
            >
              {m.badge && (
                <span className="absolute -top-2.5 right-3 rounded-full bg-accent px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-white">
                  {m.badge}
                </span>
              )}
              <p className="font-display text-sm font-extrabold uppercase">{m.title}</p>
              <p className="mt-1 font-display text-2xl font-black text-accent">{m.price}</p>
            </button>
          ))}
        </div>
      </div>

      {form.modality === "single" && (
        <div>
          <p className={labelCls}>¿Qué workshop eliges? *</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              { id: "first", label: SESSION_LABEL.first, rem: config?.remaining_single_first },
              { id: "second", label: SESSION_LABEL.second, rem: config?.remaining_single_second },
            ].map((s) => (
              <button
                type="button"
                key={s.id}
                data-testid={`wk-session-${s.id}`}
                onClick={() => setForm((f) => ({ ...f, session: s.id }))}
                disabled={s.rem === 0}
                className={`rounded-xl border-2 p-4 text-left text-sm font-bold transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50 ${
                  form.session === s.id ? "border-accent bg-accent/10 text-white" : "border-white/10 bg-card text-zinc-300"
                }`}
              >
                {s.label}
                <span className={`mt-1 block font-mono text-[10px] font-bold uppercase tracking-[0.15em] ${s.rem === 0 ? "text-red-400" : "text-accent"}`}>
                  {s.rem === 0 ? "Completo" : s.rem == null ? "Aforo: 30 personas por clase" : `${s.rem} plazas libres`}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {minor && (
        <div className="space-y-4 rounded-xl border border-amber-400/40 bg-amber-400/5 p-5" data-testid="wk-minor-block">
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-amber-300">
            Participante menor de 18 años — autorización del tutor legal
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input required data-testid="wk-guardian-name" value={form.guardian_name} onChange={set("guardian_name")} placeholder="Nombre y apellidos del tutor" className={inputCls} />
            <input required data-testid="wk-guardian-email" type="email" value={form.guardian_email} onChange={set("guardian_email")} placeholder="Email del responsable" className={inputCls} />
            <input required data-testid="wk-guardian-phone" type="tel" value={form.guardian_phone} onChange={set("guardian_phone")} placeholder="Teléfono del responsable" className={`${inputCls} sm:col-span-2`} />
          </div>
          <label className="flex items-start gap-3 text-sm text-zinc-300">
            <input type="checkbox" required data-testid="wk-guardian-accept" checked={form.guardian_accepted} onChange={set("guardian_accepted")} className="mt-1 h-4 w-4 accent-[#FF007A]" />
            Como padre/madre/tutor legal, autorizo la participación del menor en el workshop.
          </label>
        </div>
      )}

      <div className="space-y-3 rounded-xl border border-white/10 bg-card p-5">
        <label className="flex items-start gap-3 text-sm text-zinc-300">
          <input type="checkbox" required data-testid="wk-accept-terms" checked={form.accept_terms} onChange={set("accept_terms")} className="mt-1 h-4 w-4 accent-[#FF007A]" />
          Acepto las condiciones de inscripción del workshop. *
        </label>
        <label className="flex items-start gap-3 text-sm text-zinc-300">
          <input type="checkbox" required data-testid="wk-accept-privacy" checked={form.accept_privacy} onChange={set("accept_privacy")} className="mt-1 h-4 w-4 accent-[#FF007A]" />
          Acepto la política de privacidad. *
        </label>
        <label className="flex items-start gap-3 text-sm text-zinc-300">
          <input type="checkbox" data-testid="wk-consent-images" checked={form.consent_images} onChange={set("consent_images")} className="mt-1 h-4 w-4 accent-[#FF007A]" />
          Consentimiento para el uso de imágenes (opcional, independiente de las condiciones anteriores).
        </label>
      </div>

      <div className="flex flex-col items-center justify-between gap-5 rounded-xl border border-accent/40 bg-accent/5 p-5 sm:flex-row">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">Total a pagar</p>
          <p className="font-display text-4xl font-black text-accent">{total}€</p>
        </div>
        <button
          type="submit"
          data-testid="wk-submit"
          disabled={sending || !form.modality}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-accent px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_28px_rgba(255,0,122,0.45)] transition-all duration-300 hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          Book your workshop · Reserva tu plaza
        </button>
      </div>
      <p className="text-center text-[11px] text-zinc-500">
        * Campos obligatorios. La plaza se confirma exclusivamente cuando se verifica el pago. Nunca almacenamos datos de tarjeta.
      </p>
    </form>
  );
}
